@if (loading()) {
  <div class="flex items-center gap-3 p-6 text-sm text-muted-foreground">
    <hlm-spinner size="20" /> Loading contacts...
  </div>
}

@if (error(); as err) {
  <div class="mx-auto max-w-[1400px]">
    <cmt-error-state
      title="Failed to load contacts"
      [message]="err"
      (retry)="loadContacts()" />
  </div>
}

@if (!loading() && !error()) {
  <div class="mx-auto flex max-w-[1400px] flex-col gap-6">

    <!-- HERO CARD -->
    <div class="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex min-w-0 flex-col gap-1">
          <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
            Contacts
          </p>
          <h1 class="font-display text-2xl font-semibold leading-tight text-foreground">
            Contact List
          </h1>
          <p class="text-sm text-muted-foreground">
            {{ contacts().length }} contact{{ contacts().length !== 1 ? 's' : '' }}
          </p>
        </div>
        <div class="flex flex-none items-center gap-2 pt-1">
          <button hlmBtn variant="default" (click)="openAddContactDialog()" [disabled]="isAnyEditActive()">
            <hlm-icon name="lucidePlus" size="0.9rem" /> Add Contact
          </button>
        </div>
      </div>
    </div>

    <!-- Filter input (#10) — shown when > 5 contacts -->
    @if (contacts().length > 5) {
      <div class="relative">
        <hlm-icon name="lucideSearch" size="0.9rem" class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input hlmInput size="sm" class="pl-9 pr-9" placeholder="Filter contacts..."
          [value]="filterText()"
          (input)="filterText.set($any($event.target).value)" />
        @if (filterText()) {
          <button hlmBtn variant="ghost" size="icon" class="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
            (click)="filterText.set('')" type="button" tabindex="-1">
            <hlm-icon name="lucideX" size="0.8rem" />
          </button>
        }
      </div>
    }

    <!-- Contact cards (expandable) -->
    @for (contact of filteredContacts(); track contact.id) {
      <article class="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <!-- Header (clickable) -->
        <button type="button" (click)="isExpanded(contact.id) ? onPanelClosed(contact.id) : onPanelOpened(contact.id)"
          class="flex w-full items-center gap-4 px-5 py-2 text-left transition-colors hover:bg-muted/30"
          [attr.aria-expanded]="isExpanded(contact.id)">
          <span class="font-display text-lg font-medium text-foreground">
            {{ contactDisplayNames().get(contact.id) }}
          </span>

          <hlm-badge [variant]="contact.isPerson ? 'default' : 'secondary'">
            {{ contact.isPerson ? 'Person' : 'Organization' }}
          </hlm-badge>

          @if (contact.notifyDefaultDestination) {
            <hlm-badge variant="accent">Notify Default</hlm-badge>
          }

          <span class="flex-1"></span>

          <hlm-icon
            [name]="isExpanded(contact.id) ? 'lucideChevronUp' : 'lucideChevronDown'"
            size="1rem" class="text-muted-foreground" />
        </button>

        <!-- Body -->
        @if (isExpanded(contact.id)) {
          <div class="border-t border-border">

            <div class="grid gap-4 lg:grid-cols-2">

            <!-- Contact Details section (left column) -->
            <div class="p-5">
              <header class="mb-4 flex items-center gap-2.5">
                <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                  <hlm-icon [name]="contact.isPerson ? 'lucideUser' : 'lucideBuilding2'" size="1rem" />
                </span>
                <h2 class="font-display text-lg font-medium text-foreground">Contact Details</h2>
              </header>

              @if (editingContactId() === contact.id) {
                <!-- Inline edit form -->
                <form [formGroup]="contactForm" (ngSubmit)="saveContact()" class="flex flex-col gap-3">
                  <div class="py-1">
                    <hlm-switch
                      [checked]="contactForm.controls.isPerson.value"
                      (checkedChange)="contactForm.controls.isPerson.setValue($any($event))"
                      [label]="contactForm.controls.isPerson.value ? 'Person' : 'Organization'"
                    />
                  </div>

                  @if (contactForm.controls.isPerson.value) {
                    <div class="grid grid-cols-2 gap-3">
                      <hlm-form-field>
                        <label hlmLabel>Salutation</label>
                        <input hlmInput formControlName="salutation" />
                      </hlm-form-field>
                      <hlm-form-field>
                        <label hlmLabel>Title</label>
                        <input hlmInput formControlName="title" />
                      </hlm-form-field>
                    </div>
                    <div class="grid grid-cols-2 gap-3">
                      <hlm-form-field>
                        <label hlmLabel>First Name</label>
                        <input hlmInput formControlName="firstName" />
                      </hlm-form-field>
                      <hlm-form-field>
                        <label hlmLabel>Last Name</label>
                        <input hlmInput formControlName="lastName" />
                      </hlm-form-field>
                    </div>
                  } @else {
                    <hlm-form-field>
                      <label hlmLabel>Organization Name</label>
                      <input hlmInput formControlName="name" />
                    </hlm-form-field>
                  }

                  <hlm-form-field>
                    <label hlmLabel>Email</label>
                    <input hlmInput formControlName="email" />
                  </hlm-form-field>
                  <hlm-form-field>
                    <label hlmLabel>Description</label>
                    <input hlmInput formControlName="description" />
                  </hlm-form-field>
                  <div class="grid grid-cols-2 gap-3">
                    <hlm-form-field>
                      <label hlmLabel>Department</label>
                      <input hlmInput formControlName="department" />
                    </hlm-form-field>
                    <hlm-form-field>
                      <label hlmLabel>Position</label>
                      <input hlmInput formControlName="position" />
                    </hlm-form-field>
                  </div>
                  <div class="py-1">
                    <hlm-switch
                      [checked]="contactForm.controls.notifyDefaultDestination.value"
                      (checkedChange)="contactForm.controls.notifyDefaultDestination.setValue($any($event))"
                      label="Notify default destinations"
                    />
                  </div>

                  <div class="flex flex-wrap items-center gap-2 pt-2">
                    <button hlmBtn variant="default" type="submit" [disabled]="contactForm.invalid || contactSaving()">
                      @if (contactSaving()) {
                        <hlm-spinner size="16" /> Saving...
                      } @else {
                        <hlm-icon name="lucideCheck" size="0.9rem" /> Save Changes
                      }
                    </button>
                    <button hlmBtn variant="outline" type="button" (click)="cancelContactForm()">Cancel</button>
                  </div>
                </form>
              } @else {
                <!-- Read-only display -->
                <dl class="flex flex-col divide-y divide-border/70">
                  @if (contact.isPerson) {
                    <div class="flex items-center justify-between gap-4 py-2.5">
                      <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Salutation</dt>
                      <dd class="text-sm font-medium text-foreground">{{ contact.salutation || '—' }}</dd>
                    </div>
                    <div class="flex items-center justify-between gap-4 py-2.5">
                      <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Title</dt>
                      <dd class="text-sm font-medium text-foreground">{{ contact.title || '—' }}</dd>
                    </div>
                    <div class="flex items-center justify-between gap-4 py-2.5">
                      <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">First Name</dt>
                      <dd class="text-sm font-medium text-foreground">{{ contact.firstName || '—' }}</dd>
                    </div>
                    <div class="flex items-center justify-between gap-4 py-2.5">
                      <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Last Name</dt>
                      <dd class="text-sm font-medium text-foreground">{{ contact.lastName || '—' }}</dd>
                    </div>
                  } @else {
                    <div class="flex items-center justify-between gap-4 py-2.5">
                      <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Name</dt>
                      <dd class="text-sm font-medium text-foreground">{{ contact.name || '—' }}</dd>
                    </div>
                  }
                  <div class="flex items-center justify-between gap-4 py-2.5">
                    <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Email</dt>
                    <dd class="text-sm font-medium text-foreground">{{ contact.email || '—' }}</dd>
                  </div>
                  <div class="flex items-center justify-between gap-4 py-2.5">
                    <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Description</dt>
                    <dd class="text-sm font-medium text-foreground">{{ contact.description || '—' }}</dd>
                  </div>
                  <div class="flex items-center justify-between gap-4 py-2.5">
                    <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Department</dt>
                    <dd class="text-sm font-medium text-foreground">{{ contact.department || '—' }}</dd>
                  </div>
                  <div class="flex items-center justify-between gap-4 py-2.5">
                    <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Position</dt>
                    <dd class="text-sm font-medium text-foreground">{{ contact.position || '—' }}</dd>
                  </div>
                </dl>

                <div class="mt-4 flex flex-wrap items-center gap-2">
                  <button hlmBtn variant="default" (click)="startEditContact(contact)" [disabled]="isAnyEditActive()">
                    <hlm-icon name="lucidePencil" size="0.9rem" /> Edit Contact
                  </button>
                  <button hlmBtn variant="destructive" (click)="deleteContact(contact)" [disabled]="isAnyEditActive()">
                    <hlm-icon name="lucideTrash2" size="0.9rem" /> Delete
                  </button>
                </div>
              }
            </div>

            <!-- Right column: targets -->
            <div class="flex flex-col gap-4">

            <!-- Forwarding Targets -->
            <div class="p-5 lg:pt-5">
              <header class="mb-4 flex items-center gap-2.5">
                <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                  <hlm-icon name="lucidePhoneForwarded" size="1rem" />
                </span>
                <h2 class="font-display text-lg font-medium text-foreground">Forwarding Targets</h2>
                <span class="rounded-full bg-muted px-2 py-0.5 font-mono-feat text-sm text-muted-foreground">{{ contact.forwardingTargets.length }}</span>
              </header>

              @if (contact.forwardingTargets.length === 0) {
                <div class="rounded-xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
                  No forwarding targets
                </div>
              } @else {
                <!-- Header row -->
                <div class="mb-1 grid grid-cols-[1fr_1fr_80px_60px_60px_auto] items-center gap-2 px-2 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
                  <span>Name</span>
                  <span>Phone</span>
                  <span>Type</span>
                  <span>Public</span>
                  <span>Emerg.</span>
                  <span></span>
                </div>
                <div class="flex flex-col divide-y divide-border/70">
                  @for (target of contact.forwardingTargets; track target.id; let i = $index) {
                    <div class="grid grid-cols-[1fr_1fr_80px_60px_60px_auto] items-center gap-2 px-2 py-2"
                      [class]="!target.public ? 'bg-destructive/5' : ''">
                      <span class="text-sm font-medium text-foreground">{{ target.name }}</span>
                      <span class="font-mono-feat text-sm text-muted-foreground">{{ target.value }}</span>
                      <span class="text-sm text-muted-foreground">{{ target.type }}</span>
                      <span>
                        <hlm-icon [name]="target.public ? 'lucideCircleCheck' : 'lucideCircleX'" size="0.9rem"
                          [class]="target.public ? 'text-success' : 'text-muted-foreground/50'" />
                      </span>
                      <span>
                        <hlm-icon [name]="target.emergency ? 'lucideCircleCheck' : 'lucideCircleX'" size="0.9rem"
                          [class]="target.emergency ? 'text-success' : 'text-muted-foreground/50'" />
                      </span>
                      <span class="flex items-center gap-1">
                        <button hlmBtn variant="ghost" size="icon" class="h-7 w-7"
                          [disabled]="i === 0 || isAnyEditActive()"
                          (click)="moveTargetUp(contact, target)" hlmTooltip="Move up">
                          <hlm-icon name="lucideChevronUp" size="0.8rem" />
                        </button>
                        <button hlmBtn variant="ghost" size="icon" class="h-7 w-7"
                          [disabled]="i === contact.forwardingTargets.length - 1 || isAnyEditActive()"
                          (click)="moveTargetDown(contact, target)" hlmTooltip="Move down">
                          <hlm-icon name="lucideChevronDown" size="0.8rem" />
                        </button>
                        <button hlmBtn variant="ghost" size="icon" class="h-7 w-7"
                          (click)="openEditTargetDialog(contact, target, 'forwarding')"
                          [disabled]="isAnyEditActive()" hlmTooltip="Edit">
                          <hlm-icon name="lucidePencil" size="0.8rem" />
                        </button>
                        <button hlmBtn variant="ghost" size="icon" class="h-7 w-7 text-destructive"
                          (click)="deleteTarget(contact, target, 'forwarding')"
                          [disabled]="isAnyEditActive()" hlmTooltip="Delete">
                          <hlm-icon name="lucideTrash2" size="0.8rem" />
                        </button>
                      </span>
                    </div>
                  }
                </div>
              }

              <div class="mt-3">
                <button hlmBtn variant="default" (click)="openAddTargetDialog(contact, 'forwarding')" [disabled]="isAnyEditActive()">
                  <hlm-icon name="lucidePlus" size="0.9rem" /> Add Forwarding Target
                </button>
              </div>
            </div>

            <!-- Notification Targets -->
            <div class="p-5 pt-0">
              <header class="mb-4 flex items-center gap-2.5">
                <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
                  <hlm-icon name="lucideBell" size="1rem" />
                </span>
                <h2 class="font-display text-lg font-medium text-foreground">Notification Targets</h2>
                <span class="rounded-full bg-muted px-2 py-0.5 font-mono-feat text-sm text-muted-foreground">{{ contact.notificationTargets.length }}</span>
              </header>

              @if (contact.notificationTargets.length === 0) {
                <div class="rounded-xl border border-dashed border-border bg-card/50 p-6 text-center text-sm text-muted-foreground">
                  No notification targets
                </div>
              } @else {
                <!-- Header row -->
                <div class="mb-1 grid grid-cols-[1fr_1fr_80px_100px_70px_auto] items-center gap-2 px-2 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
                  <span>Name</span>
                  <span>Value</span>
                  <span>Type</span>
                  <span>Media Type</span>
                  <span>Urgent</span>
                  <span></span>
                </div>
                <div class="flex flex-col divide-y divide-border/70">
                  @for (target of contact.notificationTargets; track target.id) {
                    <div class="grid grid-cols-[1fr_1fr_80px_100px_70px_auto] items-center gap-2 px-2 py-2"
                      [class]="!target.public ? 'bg-destructive/5' : ''">
                      <span class="text-sm font-medium text-foreground">{{ target.name }}</span>
                      <span class="font-mono-feat text-sm text-muted-foreground">{{ target.value }}</span>
                      <span class="text-sm text-muted-foreground">{{ target.type }}</span>
                      <span class="text-sm text-muted-foreground">{{ target.notificationMediaType || '—' }}</span>
                      <span>
                        <hlm-icon [name]="target.urgentOnly ? 'lucideCircleCheck' : 'lucideCircleX'" size="0.9rem"
                          [class]="target.urgentOnly ? 'text-success' : 'text-muted-foreground/50'" />
                      </span>
                      <span class="flex items-center gap-1">
                        <button hlmBtn variant="ghost" size="icon" class="h-7 w-7"
                          (click)="openEditTargetDialog(contact, target, 'notification')"
                          [disabled]="isAnyEditActive()" hlmTooltip="Edit">
                          <hlm-icon name="lucidePencil" size="0.8rem" />
                        </button>
                        <button hlmBtn variant="ghost" size="icon" class="h-7 w-7 text-destructive"
                          (click)="deleteTarget(contact, target, 'notification')"
                          [disabled]="isAnyEditActive()" hlmTooltip="Delete">
                          <hlm-icon name="lucideTrash2" size="0.8rem" />
                        </button>
                      </span>
                    </div>
                  }
                </div>
              }

              <div class="mt-3">
                <button hlmBtn variant="default" (click)="openAddTargetDialog(contact, 'notification')" [disabled]="isAnyEditActive()">
                  <hlm-icon name="lucidePlus" size="0.9rem" /> Add Notification Target
                </button>
              </div>
            </div>

            </div> <!-- /right column -->
            </div> <!-- /grid -->

            <!-- Not-public hint -->
            <div class="border-t border-border px-5 py-3">
              <div class="flex items-center gap-2 text-sm text-muted-foreground">
                <span class="inline-block h-3 w-3 rounded-sm bg-destructive/10" aria-hidden="true"></span>
                <hlm-icon name="lucideInfo" size="0.8rem" />
                <span><strong>Not public</strong> — must not be disclosed by the agent.</span>
              </div>
            </div>

          </div>
        }
      </article>
    }

    @if (filteredContacts().length === 0 && contacts().length > 0 && filterText()) {
      <div class="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
        No contacts match "{{ filterText() }}"
      </div>
    }

    @if (contacts().length === 0 && !loading()) {
      <div class="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center text-sm text-muted-foreground">
        No contacts for this phone service
      </div>
    }
  </div>
}
